import { useEffect } from "react";

/**
 * Lightweight per-route SEO writer. Updates document.title plus the
 * Open-Graph + Twitter + canonical tags whenever a route mounts. No external
 * dependency — just a tiny DOM patcher.
 *
 * Usage:
 *   useSEO({
 *     title: "History — Popoyo.app",
 *     description: "...",
 *     image: "https://popoyo.app/icons/icon-512.png",   // optional, defaults
 *     path:  "/history",                              // appended to PUBLIC_BASE
 *   });
 */
const PUBLIC_BASE = "https://popoyo.app";
const DEFAULT_IMAGE = `${PUBLIC_BASE}/icons/icon-512.png`;

function upsertMeta(selector, attr, value) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/^meta\[(name|property)="([^"]+)"\]$/) || [];
    if (!key || !name) return;
    el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function upsertCanonical(url) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export function useSEO({ title, description, image, path } = {}) {
  useEffect(() => {
    const url = path ? `${PUBLIC_BASE}${path.startsWith("/") ? path : `/${path}`}` : PUBLIC_BASE;
    const img = image || DEFAULT_IMAGE;
    if (title) document.title = title;
    if (description) upsertMeta('meta[name="description"]', "content", description);
    if (title) {
      upsertMeta('meta[property="og:title"]', "content", title);
      upsertMeta('meta[name="twitter:title"]', "content", title);
    }
    if (description) {
      upsertMeta('meta[property="og:description"]', "content", description);
      upsertMeta('meta[name="twitter:description"]', "content", description);
    }
    upsertMeta('meta[property="og:image"]', "content", img);
    upsertMeta('meta[name="twitter:image"]', "content", img);
    upsertMeta('meta[property="og:url"]', "content", url);
    upsertCanonical(url);
  }, [title, description, image, path]);
}

export { PUBLIC_BASE };
